import SaveIcon from '@mui/icons-material/Save';
import {
    Box,
    Chip,
    CircularProgress,
    Paper,
    styled,
    Typography
} from '@mui/material';
import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { ApiContext } from './ApiProvider';
import DynamicFields from './DynamicFields';
import StyledButton from './StyledButton';
import ConfirmationDialog from './ConfirmationDialog';
import { useParticipantStatuses } from '../hooks/useParticipantStatuses';
import { getParticipantByIndex, isLeadBooker, getAllParticipants } from '../utils/booking';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getRealEmail = (user) => {
    if (!user?.email) return null;
    const tier = user.primary_email_tier;
    const isFake = (tier !== undefined && tier !== null) ? tier !== 0 : !!user.email_is_fake;
    return isFake ? null : user.email.toLowerCase();
};

const StatusChip = styled(Chip, {
    shouldForwardProp: (prop) => prop !== 'participantStatus'
})(({ theme, participantStatus }) => ({
    marginLeft: theme.spacing(1),
    backgroundColor: participantStatus === 'Pre-Checked-in' ? theme.palette.success.main : theme.palette.incomplete.main,
    color: participantStatus === 'Pre-Checked-in' ? theme.palette.success.contrastText : theme.palette.incomplete.text,
    size: 'small',
}));

const ParticipantStep = React.memo(({ participantIndex, onSaveAndContinue, isReadOnly }) => {
    const { booking, saveParticipant, skipParticipant, checkInParticipant, isActive, preCheckSettings, updateCurrentFormData, clearCurrentFormData, setParticipantSkipped, clearParticipantSkipped, markParticipantAsSaved } = useContext(ApiContext);
    const { enqueueSnackbar } = useSnackbar();
    const participant = getParticipantByIndex(booking, participantIndex);
    const isLeadBookerStep = isLeadBooker(participantIndex);

    // Get participant status based on checked_in flag
    const getParticipantStatus = useCallback(() => {
        if (isLeadBookerStep) {
            return booking?.checked_in ? 'Pre-Checked-in' : 'Not pre-checked-in';
        } else {
            return participant?.checked_in ? 'Pre-Checked-in' : 'Not pre-checked-in';
        }
    }, [isLeadBookerStep, booking?.checked_in, participant?.checked_in]);

    const participantStatus = getParticipantStatus();

    // Get appropriate fields for this participant type early
    const fields = useMemo(() => {
        const originalFields = isLeadBookerStep
            ? preCheckSettings?.lead_booker_fields || []
            : preCheckSettings?.party_member_fields || [];

        let processedFields = isLeadBookerStep
            ? originalFields.filter(field => field.id !== 'is_minor')
            : [...originalFields];

        // Find is_minor field and move it to the beginning
        const isMinorIndex = processedFields.findIndex(field => field.id === 'is_minor');
        if (isMinorIndex > 0) { // Only reorder if is_minor is not already first
            const isMinorField = processedFields[isMinorIndex];
            const otherFields = processedFields.filter((field, index) => index !== isMinorIndex);
            processedFields = [isMinorField, ...otherFields];
        }

        // Find custom_note field and move it to the end
        const customNoteIndex = processedFields.findIndex(field => field.id === 'custom_note');
        if (customNoteIndex !== -1 && customNoteIndex < processedFields.length - 1) { // Only reorder if custom_note is not already last
            const customNoteField = processedFields[customNoteIndex];
            const otherFields = processedFields.filter((field, index) => index !== customNoteIndex);
            processedFields = [...otherFields, customNoteField];
        }

        return processedFields;
    }, [isLeadBookerStep, preCheckSettings]);

    // Real (non-fake) emails of all other participants for uniqueness validation
    const otherPartyEmails = useMemo(() => {
        return getAllParticipants(booking)
            .filter((_, idx) => idx !== participantIndex)
            .map(p => getRealEmail(p.user))
            .filter(Boolean);
    }, [booking, participantIndex]);

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        passport_number: '',
        emergency_contact: '',
        custom_note: '',
        date_of_birth: '',
        minor: false,
        notes: ''
    });

    // Track initial data to detect changes
    const [initialFormData, setInitialFormData] = useState(null);

    // Track if data has been initialized to prevent flicker
    const [isDataInitialized, setIsDataInitialized] = useState(false);

    // Track validation errors for fields
    const [validationErrors, setValidationErrors] = useState({});

    // Fields the user has focused and left — required hint only after blur
    const [touchedFields, setTouchedFields] = useState({});

    const handleFieldBlur = useCallback((fieldId) => {
        setTouchedFields(prev => (prev[fieldId] ? prev : { ...prev, [fieldId]: true }));
    }, []);

    // Confirmation dialog state
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        type: null
    });

    // Use participant statuses hook with current form data for status updates
    useParticipantStatuses(formData, participantIndex);

    // Update context with current form data whenever it changes
    useEffect(() => {
        if (isDataInitialized) {
            updateCurrentFormData(formData, participantIndex);
        }
    }, [formData, participantIndex, isDataInitialized, updateCurrentFormData]);

    // Clear context data when component unmounts or participant changes
    useEffect(() => {
        return () => {
            clearCurrentFormData();
        };
    }, [participantIndex, clearCurrentFormData]);

    // Live email validation: format and uniqueness within the party
    useEffect(() => {
        if (!isDataInitialized) return;
        const emailField = fields.find(f => f.id === 'email');
        if (!emailField || formData.is_minor) return;

        const timer = setTimeout(() => {
            const email = (formData.email || '').trim();
            let error = null;

            if (email) {
                if (!EMAIL_REGEX.test(email)) {
                    error = '* Please enter a valid email address';
                } else if (otherPartyEmails.includes(email.toLowerCase())) {
                    error = '* This email is already used by another party member';
                }
            }

            setValidationErrors(prev => {
                if (error) return { ...prev, email: error };
                if (prev.email === '* Please enter a valid email address' || prev.email === '* This email is already used by another party member') {
                    const next = { ...prev };
                    delete next.email;
                    return next;
                }
                return prev;
            });
        }, 400);

        return () => clearTimeout(timer);
    }, [formData.email, formData.is_minor, fields, isDataInitialized, otherPartyEmails]);

    const blockMessageRef = useRef(null);
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [participantIndex]);

    useEffect(() => {
        setIsDataInitialized(false);
        // Clear validation errors when switching participants
        setValidationErrors({});
        setTouchedFields({});

        if (participant && fields) {
            const newFormData = {
                first_name: participant.user?.first_name || '',
                last_name: participant.user?.last_name || '',
                email: (() => {
                    const primaryEmailTier = participant.user?.primary_email_tier;
                    const shouldShowEmail = (primaryEmailTier !== undefined && primaryEmailTier !== null)
                        ? primaryEmailTier === 0
                        : !participant.user?.email_is_fake;
                    return shouldShowEmail ? (participant.user?.email || '') : '';
                })(),
                phone: participant.user?.phone || '',
                address: participant.user?.address || '',
                passport_number: participant.passport_number || participant.user?.passport_number || '',
                emergency_contact: participant.emergency_contact || participant.user?.emergency_contact || '',
                custom_note: participant.custom_note || participant.user?.custom_note || '',
                date_of_birth: participant.date_of_birth || participant.user?.date_of_birth || '',
                is_minor: participant.is_minor !== undefined ? participant.is_minor : (participant.user?.is_minor || false),
                notes: participant.notes || participant.user?.notes || ''
            };

            // Check if is_minor field is included in the fields configuration
            const hasIsMinorField = fields.some(field => field.id === 'is_minor');

            // If is_minor field is not in the configuration, force it to false
            if (!hasIsMinorField) {
                newFormData.is_minor = false;
            }

            // Initialize custom fields from available fields
            fields.forEach(field => {
                if (!newFormData.hasOwnProperty(field.id)) {
                    // For tag_category fields, get values differently for Lead Booker vs party members
                    if (field.type === 'tag_category' || field.type === 'category') {
                        let selectedTagIds = [];
                        // get values from tags
                        selectedTagIds = (participant.tags || [])
                            .filter(tag => tag.category?.code === field.id)
                            .map(tag => tag.id);

                        newFormData[field.id] = selectedTagIds;
                    } else {
                        newFormData[field.id] = participant[field.id] || '';
                    }
                }
            });

            setFormData(newFormData);
            setInitialFormData(newFormData);
            setIsDataInitialized(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [participantIndex, participant?.id, fields]);

    const handleChange = useCallback((event) => {
        const { name, value, type, checked } = event.target;
        const categoryField = fields.find(field => field.id === name && (field.type === 'tag_category' || field.type === 'category'));

        // Clear validation error for this field when user starts typing
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }

        if (type === 'checkbox') {
            if (name === 'is_minor') {
                // Clear validation error for is_minor field
                if (validationErrors['is_minor']) {
                    setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors['is_minor'];
                        return newErrors;
                    });
                }
                setFormData(prev => ({ ...prev, is_minor: checked }));
            } else {
                // Handle tag_category fields first
                const fieldIdMatch = name.match(/^(.+)-(.+)$/);
                if (fieldIdMatch) {
                    const [, fieldId, optionId] = fieldIdMatch;
                    const field = fields.find(f => f.id === fieldId);

                    if (field && (field.type === 'tag_category' || field.type === 'category')) {
                        // Clear validation error for tag_category field
                        if (validationErrors[fieldId]) {
                            setValidationErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[fieldId];
                                return newErrors;
                            });
                        }

                        const optionIdNum = parseInt(optionId, 10);
                        setFormData(prev => ({
                            ...prev,
                            [fieldId]: checked
                                ? [...(prev[fieldId] || []), optionIdNum]
                                : (prev[fieldId] || []).filter(item => item !== optionIdNum)
                        }));
                    }
                }
            }
        } else {
            if (categoryField && categoryField.multiselect === true && (Array.isArray(value) || typeof value === 'string')) {
                if (validationErrors[name]) {
                    setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[name];
                        return newErrors;
                    });
                }

                const selectedValues = Array.isArray(value) ? value : value.split(',');
                setFormData(prev => ({
                    ...prev,
                    [name]: selectedValues
                        .filter(item => item !== '')
                        .map(item => parseInt(item, 10))
                        .filter(item => !Number.isNaN(item))
                }));
            // For radio buttons (single-select tag_category fields)
            } else if (categoryField) {
                // Clear validation error for radio field
                if (validationErrors[name]) {
                    setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[name];
                        return newErrors;
                    });
                }
                setFormData(prev => ({ ...prev, [name]: [parseInt(value, 10)] }));
            } else {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        }
    }, [fields, validationErrors]);

    const isFormEmpty = useCallback(() => {
        if (!isDataInitialized) return true;

        return fields.every(field => {
            const value = formData[field.id];

            if (field.type === 'boolean') {
                return value === false || value === undefined;
            }

            if (field.type === 'tag_category' || Array.isArray(value)) {
                return !value || value.length === 0;
            }

            return !value || value.toString().trim() === '';
        });
    }, [formData, fields, isDataInitialized]);

    const hasFormChanges = useCallback(() => {
        if (!initialFormData || !isDataInitialized) return false;

        return fields.some(field => {
            const value = formData[field.id];
            const initialValue = initialFormData[field.id];

            if (field.type === 'tag_category' || Array.isArray(value)) {
                const currentIds = (value || []).sort();
                const initialIds = (initialValue || []).sort();
                return JSON.stringify(currentIds) !== JSON.stringify(initialIds);
            }

            return value !== initialValue;
        });
    }, [formData, initialFormData, fields, isDataInitialized]);

    const getRequiredFields = useMemo(() => {
        return fields.filter(field => field.required || field.mandatory);
    }, [fields]);

    const hasRequiredFields = useMemo(() => {
        return getRequiredFields.length > 0;
    }, [getRequiredFields]);

    const isFormValid = useMemo(() => {
        if (!isDataInitialized) return false;

        // Format/uniqueness errors always block
        if (validationErrors.email) return false;

        const requiredFields = getRequiredFields;
        const isMinor = formData.is_minor || false;

        return requiredFields.every(field => {
            if (field.type === 'boolean') {
                return true;
            }
            if (field.id === 'is_minor') {
                return true;
            }
            if (isMinor && (field.id === 'email' || field.id === 'phone')) {
                return true;
            }

            const value = formData[field.id];
            if (field.type === 'tag_category') {
                return Array.isArray(value) && value.length > 0;
            }
            return value && value.toString().trim() !== '';
        });
    }, [getRequiredFields, formData, isDataInitialized, validationErrors.email]);

    const buildParticipantPayload = useCallback(() => {
        if (!participant) {
            return null;
        }

        // For virtual participants (no sub-booking yet), send null for booking_id and user.id
        const isVirtualParticipant = participant.id === null || participant._isVirtual;

        const payload = {
            booking_id: isLeadBookerStep ? booking?.id : (isVirtualParticipant ? null : participant.id),
            user: {
                id: isVirtualParticipant ? null : (participant.user?.id || participant.id)
            },
            tags: [],
            // Include _virtualIndex for optimistic updates of virtual participants
            ...(isVirtualParticipant && { _virtualIndex: participant._virtualIndex })
        };

        const isMinor = formData.is_minor || false;

        fields.forEach(field => {
            if (field.type === 'tag_category') {
                const tagIds = formData[field.id] || [];
                // Always send tag categories — even with empty tag_ids — so backend can clear deselected tags
                payload.tags.push({
                    category_tag_id: field.category_id || field.id,
                    tag_ids: tagIds
                });
            } else {
                const value = formData[field.id];
                const isValueEmpty = value === '' || value === null || value === undefined ||
                    (Array.isArray(value) && value.length === 0);

                if (!isValueEmpty || field.type === 'string') {
                    switch (field.id) {
                        case 'first_name':
                            payload.user.first_name = value;
                            break;
                        case 'last_name':
                            payload.user.last_name = value;
                            break;
                        case 'email':
                            if (!isMinor) {
                                payload.user.email = value;
                            }
                            break;
                        case 'phone':
                            if (!isMinor) {
                                payload.user.phone = value ? value.replace(/\s/g, '') : value;
                            }
                            break;
                        case 'address':
                            payload.user.address = value;
                            break;
                        case 'passport_number':
                            payload.user.passport_number = value;
                            break;
                        case 'emergency_contact':
                            payload.user.emergency_contact = value;
                            break;
                        case 'custom_note':
                            payload.user.custom_note = value;
                            break;
                        case 'date_of_birth':
                            payload.user.date_of_birth = value;
                            break;
                        case 'is_minor':
                            payload.user.is_minor = value;
                            break;
                        case 'notes':
                            payload.user.notes = value;
                            break;
                        default:
                            payload.user[field.id] = value;
                            break;
                    }
                }
            }
        });

        return payload;
    }, [participant, isLeadBookerStep, booking?.id, fields, formData]);

    const handleSave = useCallback(async () => {
        const payload = buildParticipantPayload();
        if (!payload) {
            return;
        }

        try {
            await saveParticipant(payload);
            // Clear validation errors on successful save
            setValidationErrors({});
            enqueueSnackbar('Saved successfully', { variant: 'success' });
            // Clear skipped status if participant was skipped
            clearParticipantSkipped(participantIndex);
            // Mark participant as saved in current session
            markParticipantAsSaved(participantIndex);
            // Status will be refreshed automatically via useEffect when savedInSession changes
            // Give React time to update state before navigation
            setTimeout(() => {
                onSaveAndContinue(participantIndex);
            }, 0);
        } catch (error) {
            console.error("Failed to save participant:", error);

            // Handle validation errors (406)
            if (error.status === 406 && error.validationErrors) {
                const fieldErrors = {};
                error.validationErrors.forEach(err => {
                    fieldErrors[err.field] = err.message;
                    // Show individual error messages as toast
                    enqueueSnackbar(err.message, { variant: 'error' });
                });
                setValidationErrors(fieldErrors);
            } else {
                // For other errors, show generic error message
                enqueueSnackbar(error.message || 'Failed to save participant. Please try again.', { variant: 'error' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildParticipantPayload, saveParticipant, onSaveAndContinue, participantIndex, clearParticipantSkipped, markParticipantAsSaved, enqueueSnackbar]);

    const handleSkipMember = useCallback(async () => {
        if (!participant) {
            return;
        }

        // Build minimal payload — skip should NOT save form data
        const isVirtualParticipant = participant.id === null || participant._isVirtual;
        const skipPayload = {
            booking_id: isLeadBookerStep ? booking?.id : (isVirtualParticipant ? null : participant.id),
            user: {
                id: isVirtualParticipant ? null : (participant.user?.id || participant.id)
            },
            check_in_skipped: true,
            ...(isVirtualParticipant && { _virtualIndex: participant._virtualIndex })
        };

        try {
            // Always send skip status to backend
            await skipParticipant(skipPayload);

            // Clear any local skipped status since it's now handled by backend
            clearParticipantSkipped(participantIndex);

            // Navigate to next step
            onSaveAndContinue(participantIndex);
        } catch (error) {
            console.error("Failed to skip participant:", error);
            enqueueSnackbar(error.message || 'Failed to skip participant. Please try again.', { variant: 'error' });
        }
    }, [participant, isLeadBookerStep, booking?.id, skipParticipant, participantIndex, onSaveAndContinue, clearParticipantSkipped, enqueueSnackbar]);

    const handleSkipClick = useCallback(() => {
        const isEmpty = isFormEmpty();
        const hasChanges = hasFormChanges();
        const hasRequiredFieldsNotFilled = !isFormValid && hasRequiredFields;

        if (isEmpty) {
            setConfirmDialog({ open: true, type: 'empty' });
        } else if (hasRequiredFieldsNotFilled) {
            setConfirmDialog({ open: true, type: 'required' });
        } else if (hasChanges) {
            setConfirmDialog({ open: true, type: 'unsaved' });
        } else {
            handleSkipMember();
        }
    }, [isFormEmpty, hasFormChanges, isFormValid, hasRequiredFields, handleSkipMember]);

    const handleSaveClick = useCallback(() => {
        // Synchronously re-check email format/uniqueness to catch any race with debounce
        const emailField = fields.find(f => f.id === 'email');
        if (emailField && !(formData.is_minor)) {
            const email = (formData.email || '').trim();
            let emailError = null;
            if (email) {
                if (!EMAIL_REGEX.test(email)) {
                    emailError = '* Please enter a valid email address';
                } else if (otherPartyEmails.includes(email.toLowerCase())) {
                    emailError = '* This email is already used by another party member';
                }
            }
            if (emailError) {
                setValidationErrors(prev => ({ ...prev, email: emailError }));
                enqueueSnackbar(emailError.replace('* ', ''), { variant: 'warning' });
                return;
            }
        }

        if (!isFormValid && hasRequiredFields) {
            // Highlight missing required fields (both Lead Booker and party members)
            const errors = {};
            const isMinor = formData.is_minor || false;
            getRequiredFields.forEach(field => {
                if (field.type === 'boolean' || field.id === 'is_minor') return;
                if (isMinor && (field.id === 'email' || field.id === 'phone')) return;
                const value = formData[field.id];
                const errorMsg = field.id === 'email' ? '* Email cannot be blank' : '* Required';
                if (field.type === 'tag_category') {
                    if (!value || (Array.isArray(value) && value.length === 0)) {
                        errors[field.id] = errorMsg;
                    }
                } else if (!value || (typeof value === 'string' && value.trim() === '')) {
                    errors[field.id] = errorMsg;
                }
            });
            setValidationErrors(errors);
            setTouchedFields(prev => ({
                ...prev,
                ...Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
            }));

            if (isLeadBookerStep) {
                // Lead Booker: block save
                enqueueSnackbar('Please fill in all required fields for the Lead Booker', { variant: 'warning' });
            } else {
                // Party member: saving incomplete is allowed, but show what's missing
                setConfirmDialog({ open: true, type: 'saveIncomplete' });
            }
        } else {
            handleSave();
        }
    }, [isFormValid, hasRequiredFields, handleSave, isLeadBookerStep, getRequiredFields, formData, fields, otherPartyEmails, enqueueSnackbar]);

    const handleDialogClose = useCallback(() => {
        setConfirmDialog({ open: false, type: null });
    }, []);

    const handleDialogConfirm = useCallback(() => {
        handleDialogClose();
        if (confirmDialog.type === 'saveIncomplete') {
            handleSave();
        } else {
            handleSkipMember();
        }
    }, [handleSkipMember, handleSave, handleDialogClose, confirmDialog.type]);

    const getDialogConfig = useCallback(() => {
        const { type } = confirmDialog;

        switch (type) {
            case 'empty':
                return {
                    title: 'Skip this step?',
                    message: "You haven't entered anything yet.",
                    primaryLabel: 'SKIP MEMBER',
                    primaryVariant: 'outlined',
                    secondaryLabel: 'CANCEL',
                    secondaryVariant: 'contained'
                };
            case 'unsaved':
                return {
                    title: 'Leave this screen without saving?',
                    message: "You have unsaved changes. If you leave now, you'll lose any information you've entered.",
                    primaryLabel: 'SKIP MEMBER',
                    primaryVariant: 'outlined',
                    secondaryLabel: 'CANCEL',
                    secondaryVariant: 'contained'
                };
            case 'required':
                return {
                    title: 'Some required fields are missing',
                    message: "You can continue, but this user can't be checked in until all required info is completed.",
                    primaryLabel: 'SKIP MEMBER',
                    primaryVariant: 'outlined',
                    secondaryLabel: 'CANCEL',
                    secondaryVariant: 'contained'
                };
            case 'saveIncomplete':
                return {
                    title: 'Some required fields are missing',
                    message: "You can continue, but this user can't be checked in until all required info is completed.",
                    primaryLabel: 'SAVE AND CONTINUE',
                    primaryVariant: 'outlined',
                    secondaryLabel: 'CANCEL',
                    secondaryVariant: 'contained'
                };
            default:
                return null;
        }
    }, [confirmDialog]);

    // Check if editing is allowed for pre-checked-in participants
    // Allow editing if: participant is not pre-checked-in OR allow_edit_after_checkin is explicitly true
    const canEdit = participantStatus !== 'Pre-Checked-in' || (preCheckSettings?.allow_edit_after_checkin === true);
    const effectiveIsReadOnly = isReadOnly || !canEdit;

    // For virtual participants, only allow editing the first available (unfilled) one
    // This prevents filling virtual participants out of order
    const virtualParticipantBlockInfo = useMemo(() => {
        if (!participant?._isVirtual) {
            return { isBlocked: false, firstAvailableIndex: null };
        }

        const allParticipants = getAllParticipants(booking);

        // Find the first virtual participant (the one that should be filled first)
        const firstVirtualIndex = allParticipants.findIndex(p => p._isVirtual);

        if (firstVirtualIndex === -1) {
            return { isBlocked: false, firstAvailableIndex: null };
        }

        // Current participant is blocked if it's not the first virtual
        const isBlocked = participantIndex !== firstVirtualIndex;

        return {
            isBlocked,
            firstAvailableIndex: firstVirtualIndex
        };
    }, [participant?._isVirtual, booking, participantIndex]);

    useEffect(() => {
        if (virtualParticipantBlockInfo.isBlocked && blockMessageRef.current) {
            blockMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [virtualParticipantBlockInfo.isBlocked]);

    // Skip button is always enabled (behavior depends on waiver presence)

    // Early return if participant not found
    if (!participant) {
        return (
            <Paper elevation={0} sx={{ p: 1, bgcolor: 'background.paper' }}>
                <Typography variant="h6" color="error">
                    Participant not found (index: {participantIndex})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Total participants: {booking ? getAllParticipants(booking).length : 0}
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper elevation={0} sx={{ p: 1, pb: { xs: 10, lg: 1 }, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {isLeadBookerStep ? `Lead Booker` : 'Pre-check-in your party members'}
                    </Typography>
                    {isLeadBookerStep && <StatusChip participantStatus={participantStatus} label={participantStatus} size="small" />}
                </Box>
            </Box>

            {!isLeadBookerStep && (
                <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1, mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                            {`Guest ${participantIndex}`}
                        </Typography>
                        <StatusChip participantStatus={participantStatus} label={participantStatus} size="small" sx={{ ml: { xs: 'auto', lg: 0 } }} />
                    </Box>
                </Box>
            )}

            {virtualParticipantBlockInfo.isBlocked && (
                <Box ref={blockMessageRef} sx={{
                    position: 'sticky',
                    top: '64px',
                    zIndex: 10,
                    mt: 2,
                    mb: 2,
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    backgroundColor: 'white'
                }}>
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Fill in Guest {virtualParticipantBlockInfo.firstAvailableIndex} before proceeding to this guest.
                    </Typography>
                </Box>
            )}

            <Box sx={{ mt: 3, pt: 0.5, position: 'relative' }}>
                <DynamicFields
                    fields={fields}
                    formData={formData}
                    onChange={handleChange}
                    disabled={effectiveIsReadOnly || isActive || !isDataInitialized}
                    validationErrors={validationErrors}
                    touchedFields={touchedFields}
                    onFieldBlur={handleFieldBlur}
                />
                {!isDataInitialized && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1,
                        backdropFilter: 'blur(1px)'
                    }}>
                        <CircularProgress size={24} />
                    </Box>
                )}
                {virtualParticipantBlockInfo.isBlocked && (
                    <Box sx={{
                        position: 'absolute',
                        top: -24,
                        left: -24,
                        right: -24,
                        bottom: -24,
                        backgroundColor: 'white',
                        zIndex: 2
                    }} />
                )}
            </Box>

            {/* Desktop buttons below fields */}
            {isDataInitialized && (
                <Box sx={{ display: { xs: 'none', lg: 'flex' }, justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    {!isLeadBookerStep && participantStatus !== 'Pre-Checked-in' && !participant?._isVirtual && (
                        <StyledButton
                            variant="outlined"
                            onClick={handleSkipClick}
                            disabled={effectiveIsReadOnly || isActive}
                        >
                            Skip Member
                        </StyledButton>
                    )}
                    <StyledButton
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveClick}
                        disabled={effectiveIsReadOnly || isActive}
                    >
                        {isActive ? 'Saving...' : (participantStatus === 'Pre-Checked-in' ? 'Save' : 'Save and Continue')}
                    </StyledButton>
                </Box>
            )}

            {/* Mobile buttons at bottom */}
            {isDataInitialized && (
                <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, p: 1, zIndex: (theme) => theme.zIndex.tooltip, bgcolor: 'background.paper', borderTop: (theme) => `1px solid ${theme.palette.divider}`, display: { xs: 'flex', lg: 'none' }, flexDirection: 'column', gap: 1 }}>
                    {!isLeadBookerStep && participantStatus !== 'Pre-Checked-in' && !participant?._isVirtual && (
                        <StyledButton variant="outlined" onClick={handleSkipClick} disabled={effectiveIsReadOnly || isActive} fullWidth>
                            Skip Member
                        </StyledButton>
                    )}
                    <StyledButton variant="contained" startIcon={<SaveIcon />} onClick={handleSaveClick} disabled={effectiveIsReadOnly || isActive} fullWidth>
                        {isActive ? 'Saving...' : (participantStatus === 'Pre-Checked-in' ? 'Save' : 'Save and Continue')}
                    </StyledButton>
                </Box>
            )}

            {/* Confirmation Dialog */}
            {confirmDialog.open && getDialogConfig() && (
                <ConfirmationDialog
                    open={confirmDialog.open}
                    onClose={handleDialogClose}
                    title={getDialogConfig().title}
                    message={getDialogConfig().message}
                    primaryAction={handleDialogConfirm}
                    primaryLabel={getDialogConfig().primaryLabel}
                    primaryVariant={getDialogConfig().primaryVariant}
                    secondaryAction={handleDialogClose}
                    secondaryLabel={getDialogConfig().secondaryLabel}
                    secondaryVariant={getDialogConfig().secondaryVariant}
                />
            )}
        </Paper>
    );
});

ParticipantStep.displayName = 'ParticipantStep';

export default ParticipantStep;
